import { type DBSchema, type IDBPDatabase, openDB } from "idb";
import type { AssetFile } from "@/features/asset";
import { isOPFSAvailable } from "@/features/opfs";
import * as remote from "./remote";
import type { Project, ProjectData } from "./types";

interface ProjectDB extends DBSchema {
  projects: {
    key: string;
    value: Project;
    indexes: { "by-updated": number };
  };
}

const DB_VERSION = 1;
const dbCache = new Map<string, Promise<IDBPDatabase<ProjectDB>>>();

function getDB(userId: string) {
  let dbPromise = dbCache.get(userId);
  if (!dbPromise) {
    const dbName = `gliakinetics-${userId}`;
    dbPromise = openDB<ProjectDB>(dbName, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains("projects")) {
          const store = db.createObjectStore("projects", { keyPath: "id" });
          store.createIndex("by-updated", "updatedAt");
        }
      },
    });
    dbCache.set(userId, dbPromise);
  }
  return dbPromise;
}

/** Tracks which projects have been synced with remote in this session */
const syncedProjects = new Set<string>();

/** Get project by id - returns the newer version between local and remote */
export async function getProject(userId: string, id: string) {
  const db = await getDB(userId);
  const cacheKey = `${userId}:${id}`;

  // Already synced: only read local
  if (syncedProjects.has(cacheKey)) {
    return db.get("projects", id);
  }

  // First access: sync local + remote
  const [local, remoteProject] = await Promise.all([
    db.get("projects", id),
    remote.getProject(id).catch(() => null),
  ]);

  syncedProjects.add(cacheKey);

  // Return the newer one, save remote to local if newer
  if (remoteProject && (!local || remoteProject.updatedAt > local.updatedAt)) {
    await db.put("projects", remoteProject);
    return remoteProject;
  }

  return local;
}

/** Create a new project and save to IndexedDB + remote */
export async function createProject(userId: string, data: ProjectData) {
  const now = Date.now();
  const project: Project = {
    id: crypto.randomUUID(),
    chatState: data.chatState,
    title: data.title,
    messages: data.messages,
    files: data.files,
    dimensions: data.dimensions,
    duration: data.duration,
    totalCost: data.totalCost,
    data: data.data,
    createdAt: now,
    updatedAt: now,
  };
  const db = await getDB(userId);
  await db.put("projects", project);
  remote.saveProject(project).catch(console.warn);
  return {
    id: project.id,
    title: project.title,
    updatedAt: project.updatedAt,
    messageCount: project.messages.filter((m) => m.role === "user").length,
  };
}

/** Load all projects from remote */
export async function loadProjects() {
  return remote.listProjects();
}

/** Creates a debounced function that delays invoking fn until after wait ms */
function createDebouncedMap<T>(wait: number) {
  const pending = new Map<string, ReturnType<typeof setTimeout>>();
  return (key: string, fn: () => T) => {
    const existing = pending.get(key);
    if (existing) clearTimeout(existing);
    pending.set(
      key,
      setTimeout(() => {
        pending.delete(key);
        fn();
      }, wait),
    );
  };
}

const debouncedSync = createDebouncedMap<void>(3000);

/** Update a project by id with partial data (debounced sync to remote) */
export async function updateProject(userId: string, projectId: string, data: Partial<ProjectData>) {
  const db = await getDB(userId);
  const project = await db.get("projects", projectId);
  if (project) {
    const updated: Project = {
      ...project,
      ...data,
      updatedAt: Date.now(),
    };
    await db.put("projects", updated);
    debouncedSync(projectId, () => {
      remote.saveProject(updated).catch(console.warn);
    });
  }
}

/** Delete a project by id */
export async function deleteProject(userId: string, projectId: string) {
  const db = await getDB(userId);
  await Promise.all([
    db.delete("projects", projectId),
    deleteProjectAssets(projectId),
    remote.deleteProject(projectId),
  ]);
}

// ============================================================================
// OPFS asset storage
// ============================================================================

/** Get the projects root directory in OPFS */
async function getProjectsDirectory() {
  const available = await isOPFSAvailable();
  if (!available) throw new Error("OPFS is not available");

  const root = await navigator.storage.getDirectory();
  return root.getDirectoryHandle("projects", { create: true });
}

/** Get the project directory in OPFS */
export async function getProjectDirectory(projectId: string) {
  const projectsDir = await getProjectsDirectory();
  return projectsDir.getDirectoryHandle(projectId, { create: true });
}

/** Delete project directory from OPFS */
async function deleteProjectAssets(projectId: string) {
  const projectsDir = await getProjectsDirectory();
  await projectsDir.removeEntry(projectId, { recursive: true });
}

/** Move specified assets from one project directory to another */
export async function mvProjectAssets(
  fromDir: FileSystemDirectoryHandle,
  toDir: FileSystemDirectoryHandle,
  assets: AssetFile[],
) {
  for (const asset of assets) {
    try {
      const sourceHandle = await fromDir.getFileHandle(asset.id);
      const file = await sourceHandle.getFile();
      const targetHandle = await toDir.getFileHandle(asset.id, { create: true });
      const writable = await targetHandle.createWritable();
      await writable.write(file);
      await writable.close();
    } catch (e) {
      console.warn(`Failed to copy asset ${asset.id}:`, e);
    }
  }

  const projectsDir = await getProjectsDirectory();
  try {
    await projectsDir.removeEntry(fromDir.name, { recursive: true });
  } catch (e) {
    console.warn(`Failed to remove source directory ${fromDir.name}:`, e);
  }
}
